import { Controller } from "@hotwired/stimulus";
import { split, filter, map, uniq, some, minBy, maxBy } from 'lodash';

export default class extends Controller {
  static targets = ["tile", "highlight", "field", "middleField", "joker", "exchangeField", "makeMove", "skipTurn", "changeTiles", "gameStatus", "gameLogs", "estimatedPoints", "board"];
  static values = { playerId: Number, move: Array, selectedTileId: String, putTilesUrl: String, changeTilesUrl: String, isMoveValid: Boolean };
  static classes = [ "draggableTile", "selectedTile", "actionButtonDisabled", "makeMoveEnabled" ];

  toggleStatus() {
    this.gameStatusTarget.classList.toggle("hidden");
  }

  toggleLogs() {
    this.gameLogsTarget.classList.toggle("hidden");
  }

  boardTargetConnected() {
    this.moveValue = [];
    this.highlightLastMove();
    this.#startBoardFit();
  }

  boardTargetDisconnected() {
    this.#stopBoardFit();
  }

  disconnect() {
    this.#stopBoardFit();
  }

  highlightLastMove() {
    this.highlightTargets.forEach((tile) => {
      const originalBg = tile.style.backgroundColor;
      tile.style.transition = "background-color 0.2s ease-in-out";
      tile.style.backgroundColor = "lightgreen";

      setTimeout(() => {
        tile.style.backgroundColor = originalBg;
      }, 2000);
    })
  }

  makeMoveTargetConnected() {
    this.#updateMakeMoveButtonState();
  }

  setupJoker(e) {
    const select = e.currentTarget;
    const jokerTile = this.jokerTargets.find(t => t.id === select.dataset.jokerId)
    const setJokerUrl = select.dataset.setJokerUrl;
    const index = Number(split(select.dataset.jokerId, "-")[2]);
    const csrf = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    fetch(setJokerUrl, {
      method: "POST",
      headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
      body: JSON.stringify({index: index, letter: select.value})
    }).then((response) => {
      if (response.status === 200) {
        if (select.value === "_") {
          delete jokerTile.dataset.as;
        } else {
          jokerTile.dataset.as = select.value;
        }
        jokerTile.querySelector(".letter").innerHTML = select.value;
        this.moveValueChanged();
      }
    });
  }

  putTiles() {
    const csrf = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    const move = this.moveValue;
    
    if (!this.#isMoveValid({ alert: true })) return;

    const tiles = this.moveValue.map((c) => { 
      const field = this.fieldTargets.find(t => Number(t.dataset.row) === c.row && Number(t.dataset.col) === c.col );
      const letter = field.dataset.tile;
      const tileData = {
        row: c.row,
        col: c.col,
        letter: letter
      };

      if (letter === "_") tileData.as = field.querySelector(".tile").dataset.as;

      return tileData;
    })

    fetch(this.putTilesUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
      body: JSON.stringify({tiles: tiles})
    }).then(response => response.text()).then(text => console.log(text));
  }

  #isMoveValid(options = {}) {
    let isValid = true;

    const alertMessages = [];
    const move = this.moveValue;

    if (move.length === 0) {
      isValid = false;
      if (options.alert) alertMessages.push("Połóż litery na planszy, aby ułożyć");
    }

    if (this.#hasMoveUndefinedJokers()) {
      isValid = false;
      if (options.alert) alertMessages.push("Użyte jokery muszą być zdefiniowane");
    }

    if (this.#isFirstMove() && !this.#isMiddleIncluded()) {
      isValid = false;
      if (options.alert) alertMessages.push("Pierwszy ruch musi przecinać środkowe pole");
    }

    if (!this.#isFirstMove() && !this.#hasMoveJoin()) {
      isValid = false;
      if (options.alert) alertMessages.push("Ruch musi być przyłączony do liter na planszy");
    }

    if (this.#getMoveDirection() === "invalid") {
      isValid = false;
      if (options.alert) alertMessages.push("Ruch musi być ułożony w pionie lub poziomie");
    }

    if (this.#hasMoveBreaks()) {
      isValid = false;
      if (options.alert) alertMessages.push("Ruch nie może mieć przerw");
    }

    const alertMessage = alertMessages.join("\n");
    if (options.alert && alertMessage !== "") alert(alertMessage);
    return isValid;
  }

  changeTiles() {
    const csrf = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    const letters = this.#lettersForChange();
    if (letters.length === 0) return alert("Wybierz litery do wymiany");

    fetch(this.changeTilesUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
      body: JSON.stringify({letters: letters})
    });
  }

  #isFirstMove() {
    const middleField = this.middleFieldTarget;
    const middleFieldTile = middleField.querySelector(".tile");

    return !middleFieldTile || middleFieldTile.draggable
  }

  #isMiddleIncluded() {
    const middleField = this.middleFieldTarget;
    const middleRow = Number(middleField.dataset.row);
    const middleCol = Number(middleField.dataset.col);

    return some(this.moveValue, coords => (coords.row === middleRow && coords.col === middleCol));
  }

  #lettersForChange() {
    return filter(this.exchangeFieldTargets, f => f.dataset.tile).map(f => f.dataset.tile);
  }

  dragStart(event) {
    const tile = event.target.closest(".tile") || event.target;
    event.dataTransfer.setData("text/plain", tile.parentNode.id);
    event.dataTransfer.effectAllowed = "move";
  }

  allowDrop(event) {
    event.preventDefault();
  }

  drop(event) {
    event.preventDefault();

    const sourceField = this.fieldTargets.find(field => field.id === event.dataTransfer.getData("text/plain"))
    const targetField = event.target.closest(".field") || event.target;
    if (!sourceField || !targetField) return;

    this.#swapTiles({source: sourceField, target: targetField});
  }

  moveValueChanged() {
    const isMoveValid = this.#isMoveValid();
    this.isMoveValidValue = isMoveValid;

    if (!this.hasEstimatedPointsTarget) return;

    const estimatedPoints = this.#getEstimatedPoints({ isValid: isMoveValid });
    const target = this.estimatedPointsTargets.find(t => Number(t.dataset.playerId) === this.playerIdValue);
    if (!target) return;

    target.innerText = estimatedPoints === 0 ? "" : `+ ${estimatedPoints}`;
  }

  isMoveValidValueChanged() {
    this.#updateMakeMoveButtonState();
  }

  #updateMakeMoveButtonState() {
    if (!this.hasMakeMoveTarget) return;

    this.makeMoveTarget.disabled = !this.isMoveValidValue;

    if (this.isMoveValidValue) {
      this.makeMoveTarget.classList.remove(...this.actionButtonDisabledClasses);
      this.makeMoveTarget.classList.add(...this.makeMoveEnabledClasses);
    } else {
      this.makeMoveTarget.classList.remove(...this.makeMoveEnabledClasses);
      this.makeMoveTarget.classList.add(...this.actionButtonDisabledClasses);
    }
  }

  #getEstimatedPoints(options = { isValid: null }) {
    if (options.isValid !== null) {
      if (!options.isValid) return 0;
    } else {
      if (!this.#isMoveValid()) return 0;
    }

    const moveDirection = this.#getMoveDirection();
    const words = [];

    if (moveDirection === "right") {
      const baseWord = this.#horizontalWord(this.moveValue[0].row, this.moveValue[0].col);
      words.push(baseWord);

      this.moveValue.forEach((point) => {
        const word = this.#verticalWord(point.row, point.col);
        if (word.length > 1) words.push(word);
      })
    }

    if (moveDirection === "down") {
      const baseWord = this.#verticalWord(this.moveValue[0].row, this.moveValue[0].col);
      words.push(baseWord);

      this.moveValue.forEach((point) => {
        const word = this.#horizontalWord(point.row, point.col);
        if (word.length > 1) words.push(word);
      })
    }

    return this.#calculateMove({words: words, move: this.moveValue});
  }

  #calculateMove(data = {}) {
    const extraBonus = 50;
    const words = data.words;
    const move = data.move;

    let sum = 0;
    words.forEach((word) => {
      let wordSum = 0;
      let wordFactor = 1;

      word.forEach((coord) => {
        let letterFactor = 1;

        if (some(move, m => m.row === coord.row && m.col === coord.col)) {
          if (coord.field.dataset.perk === "true") {
            if (coord.field.dataset.perkSubject === "letter") {
              letterFactor = letterFactor * Number(coord.field.dataset.perkFactor);
            } else if (coord.field.dataset.perkSubject === "word") {
              wordFactor = wordFactor * Number(coord.field.dataset.perkFactor);
            }
          }
        }

        const tile = coord.field.querySelector(".tile");
        wordSum = wordSum + (letterFactor * Number(tile.dataset.points));
      });

      wordSum = wordSum * wordFactor
      sum = sum + wordSum;
    });

    if (move.length === 7) sum = sum + extraBonus;

    return sum;
  }

  #horizontalWord(row, col) {
    let strStart = col;
    for (let c = strStart; c >= 0; c--) {
      if (strStart === 0 || !this.#getField(row, c - 1)?.dataset.tile) break;
      strStart--
    }

    let strEnd = col;
    for (let c = strEnd; c <= 14; c++) {
      if (strEnd === 14 || !this.#getField(row, c + 1)?.dataset.tile) break;
      strEnd++
    }

    const word = [];
    for (let c = strStart; c <= strEnd; c++) {
      word.push({ row: row, col: c, field: this.#getField(row, c) })
    }

    return word;
  }

  #verticalWord(row, col) {
    let strStart = row;
    for (let r = strStart; r >= 0; r--) {
      if (strStart === 0 || !this.#getField(r - 1, col)?.dataset.tile) break;
      strStart--
    }

    let strEnd = row;
    for (let r = strEnd; r <= 14; r++) {
      if (strEnd === 14 || !this.#getField(r + 1, col)?.dataset.tile) break;
      strEnd++
    }

    const word = [];
    for (let r = strStart; r <= strEnd; r++) {
      word.push({ row: r, col: col, field: this.#getField(r, col) })
    }

    return word;
  }

  putFirst(event) {
    const targetField = event.target.closest(".field") || event.target;
    if (targetField.dataset.tile) return;

    const firstSelected = this.selectedTileIdValue && this.tileTargets.find(t => t.id === this.selectedTileIdValue)?.closest(".field");
    const firstFromRack =
      this.fieldTargets.find(field => field.dataset.dropzone === "rack" && field.dataset.tile)

    if (!firstSelected && !firstFromRack) return;

    this.#swapTiles({source: firstSelected || firstFromRack, target: targetField});
    this.#deselectTile();
  }

  putOffTiles() {
    [...this.moveValue].forEach(m => {
      const firstFieldFromMove = this.fieldTargets.find(t => Number(t.dataset.row) === m.row && Number(t.dataset.col) === m.col )
      const firstPlaceOnRack = this.fieldTargets.find(field => field.dataset.dropzone === "rack" && !field.dataset.tile)
      if (!firstFieldFromMove || !firstPlaceOnRack) return;

      this.#swapTiles({source: firstFieldFromMove, target: firstPlaceOnRack});
    });

    this.exchangeFieldTargets.forEach(f => {
      if (!f.dataset.tile) return;

      const firstPlaceOnRack = this.fieldTargets.find(field => field.dataset.dropzone === "rack" && !field.dataset.tile)
      if (!firstPlaceOnRack) return;

      this.#swapTiles({source: f, target: firstPlaceOnRack});
    });
  }

  putOffSelected(event) {
    const targetField = event.target.closest(".field") || event.target;
    if (targetField.dataset.tile) return;

    const selectedTileField = this.selectedTileIdValue && this.tileTargets.find(t => t.id === this.selectedTileIdValue)?.closest(".field");
    if (!selectedTileField) return;

    this.#swapTiles({source: selectedTileField, target: targetField});
    this.#deselectTile();
  }

  handleTileClick(event) {
    const tile = event.target.closest(".tile") || event.target;
    if (!tile.draggable) return;

    const field = tile.closest(".field");

    if (!this.selectedTileIdValue) {
      if (!field.dataset.tile) return;

      this.selectedTileIdValue = tile.id;
      tile.classList.remove(this.draggableTileClass);
      tile.classList.add(this.selectedTileClass);
    } else {
      if (this.selectedTileIdValue === tile.id) {
        this.selectedTileIdValue = undefined;
        tile.classList.add(this.draggableTileClass);
        tile.classList.remove(this.selectedTileClass);
        return;
      } else {
        const sourceField = this.#deselectTile();
        this.#swapTiles({source: sourceField, target: field})
      }
    }
  }

  #deselectTile() {
    if (!this.selectedTileIdValue) return;
    
    const selectedTile = this.tileTargets.find(t => t.id === this.selectedTileIdValue)
    this.selectedTileIdValue = undefined;
    if (!selectedTile) return;

    selectedTile.classList.add(this.draggableTileClass);
    selectedTile.classList.remove(this.selectedTileClass);

    return selectedTile.closest(".field");
  }

  #swapTiles(destinations = {}) {
    const sourceField = destinations.source;
    const targetField = destinations.target;
    if (!sourceField || !targetField) return;

    const sourceTile = sourceField.querySelector(".tile")
    const targetTile = targetField.querySelector(".tile")
    if (!sourceTile) return;

    const sourceHTML = sourceField.innerHTML;
    const targetHTML = targetField.innerHTML;

    if (targetTile && !targetTile.draggable) return;

    // Compute next move from pre-swap DOM, then update DOM before
    // assigning moveValue so validation/scoring see the new tiles.
    const nextMove = this.#moveAfterSwap({sourceField: sourceField, targetField: targetField});
    const prevMove = JSON.stringify(this.moveValue);

    if (targetTile) {
      sourceField.dataset.tile = targetTile.dataset.letter;
      sourceField.innerHTML = targetHTML;
    } else {
      delete sourceField.dataset.as;
      delete sourceField.dataset.tile;
      sourceField.innerHTML = "";
    }

    targetField.dataset.tile = sourceTile.dataset.letter;
    targetField.innerHTML = sourceHTML;

    this.moveValue = nextMove;

    // Board↔board letter swaps can keep the same coordinates; force a
    // refresh so estimated points update for the new letters.
    if (JSON.stringify(nextMove) === prevMove) {
      this.moveValueChanged();
    }
  }

  #moveAfterSwap(destinations = {}) {
    const sourceField = destinations.sourceField;
    const targetField = destinations.targetField;

    const move = [...this.moveValue];
    const moveCoords = { row: Number(targetField.dataset.row), col: Number(targetField.dataset.col) }

    if (sourceField.dataset.dropzone !== "board") {
      if (targetField.dataset.dropzone === "board") {
        if (!targetField.dataset.tile) {
          move.push(moveCoords);
        }
      }
    } else if (sourceField.dataset.dropzone === "board") {
      if (targetField.dataset.dropzone !== "board") {
        const index = move.findIndex((m) => m.row === Number(sourceField.dataset.row) && m.col === Number(sourceField.dataset.col));
        if (index !== -1) move.splice(index, 1);
      } else if (targetField.dataset.dropzone === "board") {
        if (!targetField.dataset.tile) {
          const index = move.findIndex((m) => m.row === Number(sourceField.dataset.row) && m.col === Number(sourceField.dataset.col));
          if (index !== -1) {
            move.splice(index, 1);
            move.push(moveCoords);
          }
        }
      }
    }

    return move;
  }

  #getField(row, col) {
    return this.fieldTargets.find(f => Number(f.dataset.row) === row && Number(f.dataset.col) === col);
  }

  #getNeighbourTiles(row, col) {
    return {
      top: row > 0 && !this.#isPartOfTheMove({row: row - 1, col: col}) && this.#getField(row - 1, col)?.dataset.tile,
      bottom: row < 14 && !this.#isPartOfTheMove({row: row + 1, col: col}) && this.#getField(row + 1, col)?.dataset.tile,
      left: col > 0 && !this.#isPartOfTheMove({row: row, col: col - 1}) && this.#getField(row, col - 1)?.dataset.tile,
      right: col < 14 && !this.#isPartOfTheMove({row: row, col: col + 1}) && this.#getField(row, col + 1)?.dataset.tile
    }
  }

  #isPartOfTheMove(coords = {}) {
    return !!this.moveValue.find(c => coords.row === c.row && coords.col === c.col);
  }

  #getMoveDirection() {
    if (this.moveValue.length === 1) {
      const coords = this.moveValue[0]
      const neighbours = this.#getNeighbourTiles(coords.row, coords.col)

      if (neighbours.left || neighbours.right) {
        return 'right'
      } else if (neighbours.top || neighbours.bottom) {
        return 'down'
      }

      return 'invalid'
    } else {
      return this.#isMoveHorizontal() ? 'right'
        : this.#isMoveVertical() ? 'down'
        : 'invalid'
    }
  }

  #isMoveHorizontal() {
    return uniq(map(this.moveValue, e => e.row)).length === 1;
  }

  #isMoveVertical() {
    return uniq(map(this.moveValue, e => e.col)).length === 1;
  }

  #hasMoveBreaks() {
    const direction = this.#getMoveDirection()

    if (direction === 'right') {
      const row = this.moveValue[0].row
      const minCol = minBy(this.moveValue, 'col').col
      const maxCol = maxBy(this.moveValue, 'col').col
      for (let col = minCol; col <= maxCol; col++) {
        const field = this.fieldTargets.find(t => Number(t.dataset.row) === row && Number(t.dataset.col) === col );
        if (!field?.dataset.tile) return true
      }
    } else if (direction === 'down') {
      const col = this.moveValue[0].col
      const minRow = minBy(this.moveValue, 'row').row
      const maxRow = maxBy(this.moveValue, 'row').row
      for (let row = minRow; row <= maxRow; row++) {
        const field = this.fieldTargets.find(t => Number(t.dataset.row) === row && Number(t.dataset.col) === col );
        if (!field?.dataset.tile) return true
      }
    }

    return false
  }

  #hasNeighbourTiles(row, col) {
    const neighbourValues = Object.values(this.#getNeighbourTiles(row, col))
    return some(neighbourValues)
  }

  #hasMoveJoin() {
    return some(this.moveValue, m => this.#hasNeighbourTiles(m.row, m.col))
  }

  #hasMoveUndefinedJokers() {
    return some(this.moveValue, (m) => {
      const field = this.fieldTargets.find(t => Number(t.dataset.row) === m.row && Number(t.dataset.col) === m.col );
      return field.dataset.tile === "_" && !field.querySelector(".tile").dataset.as;
    })
  }

  #startBoardFit() {
    if (!this.hasBoardTarget) return;

    const slot = this.boardTarget.closest(".game-board-slot");
    if (!slot) return;

    this.#stopBoardFit();
    this.#fitBoard();
    this.boardFitObserver = new ResizeObserver(() => this.#fitBoard());
    this.boardFitObserver.observe(slot);
  }

  #stopBoardFit() {
    this.boardFitObserver?.disconnect();
    this.boardFitObserver = null;
  }

  #fitBoard() {
    if (!this.hasBoardTarget) return;

    const board = this.boardTarget;
    const slot = board.closest(".game-board-slot");
    if (!slot) return;

    const size = Math.max(0, Math.floor(Math.min(slot.clientWidth, slot.clientHeight, 800)));
    if (size === 0) return;

    board.style.width = `${size}px`;
    board.style.height = `${size}px`;
  }
}
